import type { AccountApiResponse } from '@/types/accountApiType';

import { saleorAuthClient } from '@/graphql/client';
import { paths } from '@/lib/paths';

import type { NewsLetterSubscription } from './useNewsLetter';

export const useCatalogDownload = (authClient = saleorAuthClient) => {
  const catalogDownloadForUser = async ({
    catalogDownloadConsent,
    languageCode,
    recaptchaResponse,
    websiteCountryCode,
  }: any): Promise<AccountApiResponse<null>> => {
    const params = {
      websiteCountryCode,
      languageCode,
      newsletterOptIn: catalogDownloadConsent,
      recaptchaResponse,
    };

    try {
      const response = await authClient.fetchWithAuth(
        paths.api.catalogDownload.user,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(params),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return { code: -1, msg: 'Failed to download to chronolog' };
    }
  };

  const catalogDownloadForGuest = async ({
    catalogDownloadConsent,
    email,
    firstName,
    firstNameKanji,
    languageCode,
    lastName,
    lastNameKanji,
    recaptchaResponse,
    salutation,
    websiteCountryCode,
  }: any): Promise<AccountApiResponse<null>> => {
    const params = {
      email,
      firstName,
      lastName,
      languageCode,
      newsletterOptIn: catalogDownloadConsent,
      recaptchaResponse,
      salutation,
      websiteCountryCode,
      ...(!!firstNameKanji && { firstNameKanji }),
      ...(!!lastNameKanji && { lastNameKanji }),
    };
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.catalogDownload.guestRequest,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify(params),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return { code: -1, msg: 'Failed to download to chronolog' };
    }
  };

  const confirmCatalogDownload = async (
    emailToken: string,
  ): Promise<AccountApiResponse<NewsLetterSubscription>> => {
    try {
      const params = { emailToken };
      const response = await authClient.fetchWithAuth(
        paths.api.catalogDownload.verify,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      return response.json();
    } catch (error) {
      return {
        code: -1,
        msg: 'Failed to get the subscribe status of newsletter',
      };
    }
  };

  return {
    catalogDownloadForUser,
    catalogDownloadForGuest,
    confirmCatalogDownload,
  };
};

export type useCatalogDownload = ReturnType<typeof useCatalogDownload>;
